// src/components/leitner/TaskCreatorManager.jsx
// Two-column layout with expandable questions, all question types, marks calculation

import React, { useState, useEffect, useMemo } from 'react';
import { 
  ArrowLeft, Save, ChevronUp, ChevronDown, X, Plus, Image, Loader2, Upload,
  Globe, Users, Lock, Calendar, Trophy, Timer, Shuffle, ListOrdered, Layers,
  Eye, Mail, Award, ChevronRight, FileText
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { taskService } from '../../services/taskService';
import { leitnerService } from '../../services/leitnerService';
import { useAuth } from '../../contexts/AuthContext';
import CategoryFilter from './CategoryFilter';
import { MathRenderer } from '../math/MathRenderer';
import InlineMathField from '../math/InlineMathField';

const TaskCreatorManager = ({ bookId, questions, task, onSave, onCancel }) => {
  const { user } = useAuth();
  const isEditing = !!task;
  const [viewMode, setViewMode] = useState('creator'); // 'creator' | 'processor'
  
  // Filter state
  const [filters, setFilters] = useState({ subject: null, grade: null, topics: [] });
  const [categories, setCategories] = useState({ subjects: [], grades: [], topics: [] });
  
  // Question selection
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(task?.questionIds || []);
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);
  
  // User assignment
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Form state
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('questions'); // 'questions', 'access', 'settings'
  const [processingPages, setProcessingPages] = useState([{ id: 'page-1', content: '' }]);
  const [activeProcessingPageId, setActiveProcessingPageId] = useState('page-1');
  const [inlineMathEnabled, setInlineMathEnabled] = useState(true);
  
  const [formData, setFormData] = useState({
    name: task?.name || '',
    description: task?.description || '',
    visibility: task?.visibility || 'private',
    requiresSignIn: task?.requiresSignIn !== false, 
    assignedUsers: task?.assignedUsers || [],
    isTimeBound: task?.isTimeBound || false,
    startDate: task?.startDate ? formatDateForInput(task.startDate) : '',
    endDate: task?.endDate ? formatDateForInput(task.endDate) : '',
    passingScore: task?.passingScore || 70,
    timeLimitPerQuestion: task?.timeLimitPerQuestion || 0,
    shuffleQuestions: task?.shuffleQuestions !== false,
    showAnswersAfter: task?.showAnswersAfter !== false,
    maxAttempts: task?.maxAttempts || 0,
    displayMode: task?.displayMode || 'one-at-a-time',
    ogTitle: task?.ogTitle || '',
    ogDescription: task?.ogDescription || '',
    ogImage: task?.ogImage || '',
    creatorEmail: user?.email || ''
  });

  function formatDateForInput(date) {
    if (!date) return '';
    const d = date?.toDate?.() || new Date(date);
    return d.toISOString().slice(0, 16);
  }

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      if (bookId) {
        try {
          const cats = await leitnerService.getQuestionCategories(bookId);
          setCategories(cats);
        } catch (error) {
          console.error('Failed to load categories:', error);
        }
      }
    };
    loadCategories();
  }, [bookId]);

  // Filter questions (NO type filtering - all questions available)
  const filteredQuestions = useMemo(() => {
    return leitnerService.filterQuestionsByCategories(questions, filters);
  }, [questions, filters]);

  // Available = filtered minus already selected
  const availableQuestions = filteredQuestions.filter(q => !selectedQuestionIds.includes(q.id));

  // Selected questions in order
  const selectedQuestions = selectedQuestionIds
    .map(id => questions.find(q => q.id === id))
    .filter(Boolean);

  // Calculate total marks
  const calculateTotalMarks = () => {
    return selectedQuestionIds.reduce((total, id) => {
      const q = questions.find(question => question.id === id);
      if (!q) return total;
      if (q.type === 'stem' && q.subQuestions) {
        return total + (q.marks || q.subQuestions.length);
      }
      return total + (q.marks || 1);
    }, 0);
  };

  // Get question count (stem questions count as multiple)
  const getQuestionCount = () => {
    return selectedQuestionIds.reduce((count, id) => {
      const q = questions.find(question => question.id === id);
      if (!q) return count;
      if (q.type === 'stem' && q.subQuestions) {
        return count + q.subQuestions.length;
      }
      return count + 1;
    }, 0);
  };

  // Get question display text
  const getQuestionText = (q) => {
    if (q.type === 'stem') return q.stemText || 'Stem Question';
    return q.questionText || q.text || 'Question';
  };

  // Get type badge
  const getTypeBadge = (q) => {
    if (q.type === 'stem') {
      return (
        <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded flex items-center gap-0.5">
          <FileText size={10} />
          Stem
        </span>
      );
    }
    return null;
  };

  // Add question
  const handleAddQuestion = (id) => {
    if (!selectedQuestionIds.includes(id)) {
      setSelectedQuestionIds(prev => [...prev, id]);
    }
  };

  // Remove question
  const handleRemoveQuestion = (id) => {
    setSelectedQuestionIds(prev => prev.filter(qId => qId !== id));
  };

  // Move question up/down
  const moveQuestion = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === selectedQuestionIds.length - 1)) {
      return;
    }
    const newIds = [...selectedQuestionIds];
    const idToMove = newIds[index];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    newIds[index] = newIds[swapIndex];
    newIds[swapIndex] = idToMove;
    setSelectedQuestionIds(newIds);
  };

  // Toggle question expansion
  const toggleExpand = (id, e) => {
    e.stopPropagation();
    setExpandedQuestionId(prev => prev === id ? null : id);
  };

  // User search
  const handleUserSearch = async (query) => {
    setUserSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const results = await taskService.searchUsersByEmail(query);
      setSearchResults(results.filter(u => !formData.assignedUsers.includes(u.email)));
    } catch (error) {
      console.error('User search failed:', error);
    }
  };

  const addUser = (email) => {
    if (!formData.assignedUsers.includes(email)) {
      setFormData(prev => ({ ...prev, assignedUsers: [...prev.assignedUsers, email] }));
    }
    setUserSearch('');
    setSearchResults([]);
  };

  const addManualEmail = () => {
    const email = userSearch.trim();
    if (email && email.includes('@') && !formData.assignedUsers.includes(email)) {
      addUser(email);
    }
  };

  const removeUser = (email) => {
    setFormData(prev => ({ ...prev, assignedUsers: prev.assignedUsers.filter(e => e !== email) }));
  };

  // OG Image upload
const handleOgImageUpload = async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  setUploadingImage(true);
    try {
      const url = await taskService.uploadTaskImage(bookId, task?.id || 'new', file);
      setFormData(prev => ({ ...prev, ogImage: url }));
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveOgImage = () => {
    setFormData(prev => ({ ...prev, ogImage: '' }));
  };

  // Save task
  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a task name');
      return;
    }
    if (selectedQuestionIds.length === 0) {
      alert('Please select at least one question');
      return;
    }
    if (formData.isTimeBound && (!formData.startDate || !formData.endDate)) {
      alert('Please set both start and end dates');
      return;
    }
    
    setSaving(true);
    try {
      await onSave({
        ...formData,
        questionIds: selectedQuestionIds,
        totalMarks: calculateTotalMarks(),
        questionCount: getQuestionCount(),
        ogTitle: formData.ogTitle || formData.name,
        ogDescription: formData.ogDescription || formData.description,
        ogImage: formData.ogImage
      });
    } catch (error) {
      console.error('Failed to save task:', error);
      alert('Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  // Render expanded question content
  const renderQuestionContent = (q) => {
    if (q.type === 'stem') {
      return (
        <div className="mt-3 pl-4 border-l-2 border-gray-300 space-y-2">
          {q.stemImage && (
            <img src={q.stemImage} alt="Stem" className="max-h-32 rounded" />
          )}
          <div className="text-sm text-gray-600">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{q.stemText}</ReactMarkdown>
          </div>
          {q.subQuestions?.map((sub, idx) => (
            <div key={sub.id || idx} className="p-2 bg-gray-50 rounded text-sm">
              <span className="font-medium text-gray-700">{idx + 1}.</span>{' '}
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: React.Fragment }}>
                {sub.text}
              </ReactMarkdown>
              <div className="flex flex-wrap gap-1 mt-1">
                {sub.options?.map((opt, optIdx) => (
                  <span 
                    key={opt.id || optIdx} 
                    className={`px-2 py-0.5 text-xs rounded ${
                      sub.correctOptionIds?.includes(opt.id) 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {opt.text}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    // Simple question
    return (
      <div className="mt-3 pl-4 border-l-2 border-gray-300">
        {q.stemImage && (
          <img src={q.stemImage} alt="Question" className="max-h-32 rounded mb-2" />
        )}
        <div className="text-sm text-gray-600 mb-2">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{q.questionText || q.text}</ReactMarkdown>
        </div>
        <div className="flex flex-wrap gap-1">
          {q.options?.map((opt, idx) => (
            <span 
              key={opt.id || idx} 
              className={`px-2 py-0.5 text-xs rounded ${
                q.correctOptionIds?.includes(opt.id) 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {opt.text}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const activeProcessingPage = processingPages.find(page => page.id === activeProcessingPageId);

  const handleProcessingContentChange = (value) => {
    setProcessingPages(prev =>
      prev.map(page => page.id === activeProcessingPageId ? { ...page, content: value } : page)
    );
  };

  const addProcessingPage = () => {
    const nextIndex = processingPages.length + 1;
    const newPage = { id: `page-${Date.now()}-${nextIndex}`, content: '' };
    setProcessingPages(prev => [...prev, newPage]);
    setActiveProcessingPageId(newPage.id);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onCancel} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-5 w-5" />
          Back to Tasks
        </button>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            {[
              { id: 'creator', label: 'Creator View' },
              { id: 'processor', label: 'Task Processor' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setViewMode(tab.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                  viewMode === tab.id
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {viewMode === 'creator' && (
            <button
              onClick={handleSave}
              disabled={saving || !formData.name || selectedQuestionIds.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : isEditing ? 'Update Task' : 'Create Task'}
            </button>
          )}
        </div>
      </div>

      {viewMode === 'processor' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Task Processing</h2>
              <p className="text-sm text-gray-500">
                Paste text on an A4 canvas and preview it with inline math rendering.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={inlineMathEnabled}
                  onChange={(e) => setInlineMathEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600"
                />
                Inline math enabled
              </label>
              <button
                onClick={addProcessingPage}
                className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4" />
                Add Page
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {processingPages.map((page, index) => (
              <button
                key={page.id}
                onClick={() => setActiveProcessingPageId(page.id)}
                className={`px-3 py-1.5 text-sm rounded-full border transition ${
                  activeProcessingPageId === page.id
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 text-gray-600 hover:text-gray-900'
                }`}
              >
                Page {index + 1}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">Paste Text</h3>
                <span className="text-xs text-gray-500">
                  Use inline math like <span className="font-mono">$x^2$</span> or <span className="font-mono">\(x^2\)</span>
                </span>
              </div>
              <div className="w-full max-w-[760px] aspect-[210/297] bg-white border border-gray-200 shadow-sm rounded-lg overflow-hidden">
                <div className="w-full h-full">
                  <InlineMathField
                    value={activeProcessingPage?.content || ''}
                    onChange={handleProcessingContentChange}
                    rows={20}
                    placeholder="Paste or type your maths/science text here... (Ctrl+M for math)"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">Rendered Preview</h3>
                <span className="text-xs text-gray-500">A4 page preview</span>
              </div>
              <div className="w-full max-w-[760px] aspect-[210/297] bg-white border border-gray-200 shadow-sm rounded-lg overflow-hidden">
                <div className="w-full h-full p-6 overflow-auto">
                  <MathRenderer
                    content={activeProcessingPage?.content || ''}
                    enableMarkdown={true}
                    enableMath={inlineMathEnabled}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'creator' && (
        <>
      {/* Basic Info */}
      <div className="bg-gray-50 rounded-lg p-4 grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Task Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            placeholder="e.g., Week 1 Math Assessment"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            placeholder="Brief description..."
          />
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex border-b border-gray-200">
        {[
          { id: 'questions', label: `Questions (${selectedQuestionIds.length})` },
          { id: 'access', label: 'Access' },
          { id: 'settings', label: 'Settings' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`px-4 py-2 font-medium border-b-2 transition ${
              activeSection === tab.id
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Questions Section */}
      {activeSection === 'questions' && (
        <div className="space-y-4">
          {/* Category Filter */}
          <CategoryFilter
            categories={categories}
            selectedFilters={filters}
            onFilterChange={setFilters}
            onClear={() => setFilters({ subject: null, grade: null, topics: [] })}
            questions={questions}
          />

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Available Questions */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center justify-between">
                <span>Available Questions</span>
                <span className="text-sm text-gray-500">{availableQuestions.length} questions</span>
              </h3>
              
              <div className="space-y-2 max-h-[50vh] overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                {availableQuestions.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">
                    {questions.length === 0 ? 'No questions available.' : 'No questions match the selected filters.'}
                  </p>
                ) : (
                  availableQuestions.map(q => (
                    <div key={q.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div
                        onClick={() => handleAddQuestion(q.id)}
                        className="p-3 cursor-pointer hover:bg-gray-50 transition"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-900 line-clamp-2">
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: React.Fragment }}>
                                {getQuestionText(q)}
                              </ReactMarkdown>
                            </div>
                            <div className="flex flex-wrap items-center gap-1 mt-1.5">
                              {q.subject && <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{q.subject}</span>}
                              {q.grade && <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{q.grade}</span>}
                              {q.topics?.map(topic => (
                                <span key={topic} className="text-xs text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{topic}</span>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {getTypeBadge(q)}
                            {(q.marks > 1 || q.type === 'stem') && (
                              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded flex items-center gap-0.5">
                                <Award size={10} />
                                {q.marks || (q.type === 'stem' ? q.subQuestions?.length : 1)}
                              </span>
                            )}
                            <button
                              onClick={(e) => toggleExpand(q.id, e)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              <ChevronRight className={`h-4 w-4 transition ${expandedQuestionId === q.id ? 'rotate-90' : ''}`} />
                            </button>
                            <span className="text-xs text-indigo-600 font-medium">+ Add</span>
                          </div>
                        </div>
                      </div>
                      {expandedQuestionId === q.id && (
                        <div className="px-3 pb-3 border-t border-gray-100 bg-gray-50">
                          {renderQuestionContent(q)}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Selected Questions */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center justify-between">
                <span>Selected Questions</span>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-gray-500">{selectedQuestionIds.length} items ({getQuestionCount()} Qs)</span>
                  <span className="text-amber-600 flex items-center gap-1 font-medium">
                    <Award size={14} />
                    {calculateTotalMarks()} marks
                  </span>
                </div>
              </h3>
              
              <div className="space-y-2 max-h-[50vh] overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                {selectedQuestions.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">Click questions to add them.</p>
                ) : (
                  selectedQuestions.map((q, index) => (
                    <div key={q.id} className="bg-white rounded-lg border border-indigo-200 overflow-hidden">
                      <div className="flex items-center gap-2 p-3">
                        {/* Reorder buttons */}
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => moveQuestion(index, 'up')}
                            disabled={index === 0}
                            className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            onClick={() => moveQuestion(index, 'down')}
                            disabled={index === selectedQuestions.length - 1}
                            className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>
                        
                        <span className="text-xs text-gray-500 w-6">{index + 1}.</span>
                        
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-900 line-clamp-2">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: React.Fragment }}>
                              {getQuestionText(q)}
                            </ReactMarkdown>
                          </div>
                          <div className="flex flex-wrap items-center gap-1 mt-1">
                            {q.type === 'stem' && (
                              <span className="text-xs text-purple-600">Stem with {q.subQuestions?.length || 0} sub-questions</span>
                            )}
                            {q.topics?.slice(0, 2).map(topic => (
                              <span key={topic} className="text-xs text-indigo-600 bg-indigo-50 px-1 rounded">{topic}</span>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                            {q.marks || (q.type === 'stem' ? q.subQuestions?.length : 1)}pts
                          </span>
                          <button
                            onClick={(e) => toggleExpand(q.id, e)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleRemoveQuestion(q.id)}
                            className="p-1 text-red-400 hover:text-red-600"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                      {expandedQuestionId === q.id && (
                        <div className="px-3 pb-3 border-t border-gray-100 bg-gray-50">
                          {renderQuestionContent(q)}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Access Section */}
      {activeSection === 'access' && (
        <div className="space-y-6 max-w-2xl">
          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Who can access this task?</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'private', icon: Lock, label: 'Private', desc: 'Invited only' },
                { value: 'organisation', icon: Users, label: 'Organisation', desc: 'All members' },
                { value: 'public', icon: Globe, label: 'Public', desc: 'Anyone' }
              ].map(({ value, icon: Icon, label, desc }) => (
                <button
                  key={value}
                  onClick={() => setFormData(prev => ({ ...prev, visibility: value }))}
                  className={`p-4 rounded-lg border-2 transition text-left ${
                    formData.visibility === value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Icon className={`h-5 w-5 mb-2 ${formData.visibility === value ? 'text-indigo-600' : 'text-gray-400'}`} />
                  <div className="font-medium text-gray-900">{label}</div>
                  <div className="text-xs text-gray-500">{desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Guest Access Toggle - Only for public tasks */}
          {formData.visibility === 'public' && (
            <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!formData.requiresSignIn}
                  onChange={(e) => setFormData(prev => ({ ...prev, requiresSignIn: !e.target.checked }))}
                  className="mt-1 w-4 h-4 text-amber-500 rounded border-gray-300 focus:ring-amber-500"
                />
                <div>
                  <span className="font-medium text-amber-400">Allow guest access (no sign-in)</span>
                  <p className="text-sm text-amber-300/70 mt-1">
                    Users can take this task without logging in. Progress will only be saved locally on their device and won't sync across devices.
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* User Assignment */}
          {formData.visibility === 'private' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Assign Users by Email</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="email"
                    value={userSearch}
                    onChange={(e) => handleUserSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addManualEmail()}
                    placeholder="Search or enter email..."
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <button onClick={addManualEmail} disabled={!userSearch.includes('@')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              {searchResults.length > 0 && (
                <div className="mt-2 bg-white rounded-lg border max-h-32 overflow-y-auto">
                  {searchResults.map(r => (
                    <button key={r.id} onClick={() => addUser(r.email)} className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm">
                      {r.email} {r.displayName && <span className="text-gray-400">({r.displayName})</span>}
                    </button>
                  ))}
                </div>
              )}
              {formData.assignedUsers.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {formData.assignedUsers.map(email => (
                    <span key={email} className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 rounded-full text-sm">
                      {email}
                      <button onClick={() => removeUser(email)} className="hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Time Bound */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={formData.isTimeBound} onChange={(e) => setFormData(prev => ({ ...prev, isTimeBound: e.target.checked }))} className="w-5 h-5 rounded text-indigo-600" />
              <div>
                <span className="font-medium text-gray-900">Time-bound Task</span>
                <p className="text-sm text-gray-500">Set availability window</p>
              </div>
            </label>
            {formData.isTimeBound && (
              <div className="grid grid-cols-2 gap-4 mt-3 pl-8">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Start</label>
                  <input type="datetime-local" value={formData.startDate} onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">End</label>
                  <input type="datetime-local" value={formData.endDate} onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Section */}
      {activeSection === 'settings' && (
        <div className="space-y-6 max-w-2xl">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1"><Trophy className="h-4 w-4 inline mr-1" />Passing Score (%)</label>
              <input type="number" min="0" max="100" value={formData.passingScore} onChange={(e) => setFormData(prev => ({ ...prev, passingScore: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1"><Timer className="h-4 w-4 inline mr-1" />Time/Question (sec)</label>
              <input type="number" min="0" value={formData.timeLimitPerQuestion} onChange={(e) => setFormData(prev => ({ ...prev, timeLimitPerQuestion: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg" placeholder="0 = No limit" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Attempts</label>
              <input type="number" min="0" value={formData.maxAttempts} onChange={(e) => setFormData(prev => ({ ...prev, maxAttempts: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg" placeholder="0 = Unlimited" />
            </div>
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={formData.shuffleQuestions} onChange={(e) => setFormData(prev => ({ ...prev, shuffleQuestions: e.target.checked }))} className="w-5 h-5 rounded text-indigo-600" />
              <span className="text-gray-900"><Shuffle className="h-4 w-4 inline mr-2 text-gray-400" />Shuffle Questions</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={formData.showAnswersAfter} onChange={(e) => setFormData(prev => ({ ...prev, showAnswersAfter: e.target.checked }))} className="w-5 h-5 rounded text-indigo-600" />
              <span className="text-gray-900"><Eye className="h-4 w-4 inline mr-2 text-gray-400" />Show Answers After Completion</span>
            </label>
          </div>

          {/* Display Mode */}
          <div className="pt-4 border-t">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <Layers className="h-4 w-4 inline mr-2 text-gray-400" />
              Question Display Mode
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'one-at-a-time', label: 'One at a Time', icon: ListOrdered, desc: 'Show questions sequentially' },
                { value: 'all-at-once', label: 'All at Once', icon: Layers, desc: 'Show all questions on one page' }
              ].map(({ value, label, icon: Icon, desc }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, displayMode: value }))}
                  className={`p-3 border-2 rounded-lg text-left transition-all ${
                    formData.displayMode === value
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Icon className={`h-5 w-5 mb-1 ${formData.displayMode === value ? 'text-indigo-600' : 'text-gray-400'}`} />
                  <div className="font-medium text-gray-900 text-sm">{label}</div>
                  <div className="text-xs text-gray-500">{desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* OG Meta */}
          <div className="pt-4 border-t">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Image className="h-4 w-4 text-gray-400" />
              Share Preview (OG Meta)
            </h4>
            
            {/* OG Image Upload */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Share Image</label>
              {formData.ogImage ? (
                <div className="relative w-full max-w-md">
                  <img
                    src={formData.ogImage}
                    alt="OG Preview"
                    className="w-full h-40 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    onClick={handleRemoveOgImage}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full max-w-md h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleOgImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                  {uploadingImage ? (
                    <Loader2 className="h-8 w-8 text-indigo-500 animate-spin" />
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Click to upload share image</span>
                      <span className="text-xs text-gray-400 mt-1">Recommended: 1200x630px</span>
                    </>
                  )}
                </label>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Share Title</label>
                <input type="text" value={formData.ogTitle} onChange={(e) => setFormData(prev => ({ ...prev, ogTitle: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" placeholder={formData.name || 'Task title'} />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Share Description</label>
                <input type="text" value={formData.ogDescription} onChange={(e) => setFormData(prev => ({ ...prev, ogDescription: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" placeholder={formData.description || 'Description'} />
              </div>
            </div>

            {/* Preview Card */}
            {(formData.ogImage || formData.ogTitle || formData.ogDescription) && (
              <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">Preview</p>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden max-w-sm">
                  {formData.ogImage && (
                    <img src={formData.ogImage} alt="Preview" className="w-full h-32 object-cover" />
                  )}
                  <div className="p-3">
                    <h4 className="font-medium text-gray-900 text-sm truncate">
                      {formData.ogTitle || formData.name || 'Task Title'}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {formData.ogDescription || formData.description || 'Task description'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default TaskCreatorManager;
